<template>
  <v-container fluid class="ncrm-deal-board">
    <v-card class="pa-6">
      <div class="d-flex flex-wrap align-center justify-space-between ga-4">
        <div class="text-left">
          <h2 class="text-h5 ncrm-text-strong">
            {{ board?.name || $t("ncrm.deals.board.titleFallback") }}
          </h2>
          <div class="text-body-2 text-medium-emphasis">
            {{ $t("ncrm.deals.board.subtitle") }}
          </div>
          <div v-if="board" class="d-flex flex-wrap align-center ga-2 mt-2">
            <v-chip color="primary">
              {{ $t("ncrm.deals.board.openDeals", { count: formatNumber(board.open_deal_count) }) }}
            </v-chip>
            <v-chip variant="outlined">
              {{ $t("ncrm.deals.board.totalDeals", { count: formatNumber(board.deal_count) }) }}
            </v-chip>
          </div>
        </div>

        <div class="d-flex align-center ga-2 flex-wrap">
          <v-btn variant="text" color="primary" @click="openBoardSettingsDialog">
            <v-icon start>mdi-cog-outline</v-icon>
            {{ $t("ncrm.deals.actions.boardSettings") }}
          </v-btn>
          <v-btn variant="text" color="primary" @click="openCardLayoutDialog">
            <v-icon start>mdi-view-grid-plus-outline</v-icon>
            {{ $t("ncrm.deals.actions.cardSettings") }}
          </v-btn>
          <v-btn variant="text" color="primary" :loading="loading" @click="loadBoard">
            <v-icon start>mdi-refresh</v-icon>
            {{ $t("ncrm.deals.actions.refresh") }}
          </v-btn>
          <v-btn color="primary" @click="openCreateDealDialog">
            <v-icon start>mdi-plus</v-icon>
            {{ $t("ncrm.deals.actions.createDeal") }}
          </v-btn>
        </div>
      </div>
    </v-card>

    <v-card class="mt-4">
      <v-card-text>
        <NcrmDealBoardToolbar
          v-model:view="activeView"
          v-model:search="search"
          :view-items="viewItems"
          :preferred-view="preferredView"
          :filters="advancedFilters"
          :density-cycle-disabled="densityCycleDisabled"
          :density-cycle-aria-label="densityCycleAriaLabel"
          :density-cycle-tooltip="densityCycleTooltip"
          :density-state-icon="densityStateIcon"
          @set-default="setPreferredView"
          @open-filters="advancedFiltersDialog = true"
          @remove-filter="removeAdvancedFilter"
          @cycle-density="cycleDensity"
          @reset-filters="resetFilters"
        />

        <div
          v-if="activeView === 'board'"
          class="ncrm-deal-board__columns"
          :class="`ncrm-deal-board__columns--${effectiveDensity}`"
        >
          <section
            v-for="(column, columnIndex) in boardColumns"
            :key="`deal-stage-${column.stage_id}`"
            class="ncrm-deal-board__column"
            :class="[
              `ncrm-deal-board__column--${effectiveDensity}`,
              columnDragClasses(column),
            ]"
          >
            <div
              class="ncrm-deal-board__column-accent"
              :style="{ background: columnAccentStyle(columnIndex) }"
            />
            <header class="ncrm-deal-board__column-header">
              <span class="ncrm-text-strong">{{ column.stage_name }}</span>
              <v-chip size="x-small" :color="columnAccentColor(columnIndex)">
                {{ stageCountLabel(column) }}
              </v-chip>
            </header>

            <div
              class="ncrm-deal-board__column-body"
              @dragover.prevent="onStageDragOver($event, column)"
              @dragenter.prevent="onStageDragOver($event, column)"
              @drop.prevent="onStageDrop(column)"
            >
              <div v-if="!column.items.length" class="ncrm-deal-board__column-empty">
                <v-icon size="22" class="ncrm-deal-board__column-empty-icon">mdi-inbox-outline</v-icon>
                <span>{{ $t("ncrm.deals.board.emptyStage") }}</span>
              </div>

              <div v-else class="ncrm-deal-board__column-items">
                <v-card
                  v-for="deal in column.visible_items"
                  :key="`deal-card-${deal.id}`"
                  class="ncrm-deal-board__entry"
                  :class="[
                    `ncrm-deal-board__entry--${effectiveDensity}`,
                    { 'ncrm-deal-board__entry--dragging': draggingDealId === Number(deal.id) },
                    { 'ncrm-deal-board__entry--overdue': isNextActivityOverdue(deal.next_activity_due_at) },
                  ]"
                  variant="outlined"
                  role="button"
                  tabindex="0"
                  :draggable="isDealDraggable(deal)"
                  @click="handleDealCardActivate(deal.id)"
                  @keydown.enter.prevent="handleDealCardActivate(deal.id)"
                  @keydown.space.prevent="handleDealCardActivate(deal.id)"
                  @dragstart="onDealDragStart($event, deal, column)"
                  @dragend="onDealDragEnd"
                >
                  <v-card-text :class="dealCardPaddingClass">
                    <div class="d-flex align-start justify-space-between ga-2">
                      <div class="text-body-2 ncrm-text-strong ncrm-deal-board__entry-title" :title="deal.name || `#${deal.id}`">
                        {{ deal.name || `#${deal.id}` }}
                      </div>
                      <v-chip
                        size="x-small"
                        :color="statusColor(deal.status)"
                        class="flex-shrink-0 ncrm-deal-board__entry-status-chip"
                      >
                        {{ statusLabel(deal.status) }}
                      </v-chip>
                    </div>

                    <div class="ncrm-deal-board__entry-fields mt-1">
                      <div
                        v-for="field in resolvedDealCardFields(deal)"
                        :key="`deal-${deal.id}-field-${field.key}`"
                        class="ncrm-deal-board__entry-field"
                      >
                        <span
                          class="text-caption text-medium-emphasis ncrm-deal-board__entry-field-label"
                          :title="field.label"
                        >
                          {{ field.label }}:
                        </span>
                        <div v-if="field.variant === 'chip'" class="ncrm-deal-board__entry-field-value d-flex align-center">
                          <v-chip
                            v-if="field.value"
                            size="x-small"
                            :color="field.color || 'primary'"
                            class="ncrm-deal-board__entry-field-chip"
                          >
                            {{ field.value }}
                          </v-chip>
                          <span v-else class="text-caption text-medium-emphasis">-</span>
                        </div>
                        <span
                          v-else
                          :class="[
                            'text-body-2',
                            'ncrm-deal-board__entry-field-value',
                            { 'ncrm-deal-board__entry-field-value--overdue': field.overdue },
                          ]"
                          :title="field.value || '-'"
                        >
                          {{ field.value || "-" }}
                        </span>
                      </div>
                    </div>

                    <div class="ncrm-deal-board__entry-footer mt-2">
                      <div class="ncrm-deal-board__entry-footer-timestamp">
                        <v-icon size="11" class="ncrm-deal-board__entry-footer-icon">mdi-clock-outline</v-icon>
                        {{ formatDateTime(deal.updated_at) }}
                      </div>
                      <div class="d-flex align-center ga-1">
                        <v-btn
                          icon="mdi-chevron-left"
                          size="x-small"
                          variant="text"
                          color="primary"
                          :disabled="movingDealId === deal.id || !canMoveDeal(deal, -1)"
                          @click.stop="moveDealByOffset(deal, -1)"
                        />
                        <v-btn
                          icon="mdi-chevron-right"
                          size="x-small"
                          variant="text"
                          color="primary"
                          :disabled="movingDealId === deal.id || !canMoveDeal(deal, 1)"
                          @click.stop="moveDealByOffset(deal, 1)"
                        />
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <v-btn
                  v-if="column.has_more"
                  size="x-small"
                  variant="text"
                  color="primary"
                  class="ncrm-deal-board__load-more"
                  :disabled="loading"
                  @click="loadMoreStageDeals(column.stage_id)"
                >
                  {{ stageLoadMoreLabel(column) }}
                </v-btn>
              </div>
            </div>

            <div
              v-if="columnIndex < boardColumns.length - 1"
              class="ncrm-deal-board__column-divider"
            />
          </section>
        </div>

        <v-data-table
          v-else
          :headers="tableHeaders"
          :items="deals"
          item-key="id"
          class="elevation-0 ncrm-deal-board__table"
          density="compact"
          :loading="loading"
          :row-props="tableRowProps"
          @click:row="handleTableRowClick"
        >
          <template #item.name="{ item }">
            <div class="d-flex align-center ga-2">
              <v-icon size="14" class="ncrm-deal-board__table-cell-icon">
                {{ item.company_name ? 'mdi-domain' : item.person_name ? 'mdi-account-outline' : 'mdi-handshake-outline' }}
              </v-icon>
              <span class="text-body-2 ncrm-text-strong ncrm-deal-board__table-cell-inline" :title="item.name || `#${item.id}`">
                {{ item.name || `#${item.id}` }}
              </span>
            </div>
          </template>

          <template #item.last_call="{ item }">
            <NcrmLastCallCell :last-call-at="item.last_call_at || ''" :has-pbx="Boolean(item.last_call_has_pbx)" />
          </template>

          <template #item.stage_name="{ item }">
            <v-chip size="x-small" variant="outlined">
              {{ item.stage_name || "-" }}
            </v-chip>
          </template>

          <template #item.status="{ item }">
            <v-chip size="x-small" :color="statusColor(item.status)">
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>

          <template #item.owner_name="{ item }">
            <span class="ncrm-deal-board__table-cell-inline" :title="item.owner_name || '-'">
              {{ item.owner_name || "-" }}
            </span>
          </template>

          <template #item.company_name="{ item }">
            <span class="ncrm-deal-board__table-cell-inline" :title="item.company_name || '-'">
              {{ item.company_name || "-" }}
            </span>
          </template>

          <template #item.expected_value="{ item }">
            <span class="ncrm-deal-board__table-cell-inline" :title="formatCurrency(item.expected_value)">
              {{ formatCurrency(item.expected_value) }}
            </span>
          </template>

          <template #item.expected_close_date="{ item }">
            <span class="ncrm-deal-board__table-cell-inline" :title="formatDate(item.expected_close_date)">
              {{ formatDate(item.expected_close_date) }}
            </span>
          </template>

          <template #item.updated_at="{ item }">
            <div class="d-flex align-center ga-1 text-caption text-medium-emphasis" style="white-space: nowrap">
              <v-icon size="12" style="opacity: 0.55">mdi-clock-outline</v-icon>
              {{ formatDateTime(item.updated_at) }}
            </div>
          </template>

          <template #item.row_nav>
            <v-icon size="16" class="ncrm-deal-board__table-chevron">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <v-alert type="info" class="ma-4">
              {{ $t("ncrm.deals.board.empty") }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <NcrmDealBoardFiltersDrawer
      v-model="advancedFiltersDialog"
      :board-id="boardId"
      :filters="advancedFilters"
      @apply="applyAdvancedFilters"
    />

    <NcrmDealLostReasonDialog
      v-model="lostReasonDialog"
      :deal-name="pendingLostStageMove?.dealName || ''"
      :target-status-label="statusLabel(pendingLostStageMove?.targetStageType || 'lost')"
      :loading="movingDealId === Number(pendingLostStageMove?.dealId || 0)"
      @confirm="confirmLostReason"
      @cancelled="closeLostReasonDialog"
    />

    <v-dialog v-model="createDealDialog" max-width="760">
      <v-card>
        <v-card-title>{{ $t("ncrm.deals.board.createDeal.title") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="createDealForm.name"
                :label="$t('ncrm.deals.board.createDeal.fields.name')"
                required
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="createDealForm.owner_user_id"
                :items="ownerOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.deals.board.createDeal.fields.owner')"
                :loading="ownerLoading"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="createDealForm.stage_id"
                :items="stageOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.deals.board.createDeal.fields.stage')"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="createDealForm.product_ids"
                :items="createDealProductOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.deals.board.createDeal.fields.products')"
                multiple
                chips
                closable-chips
                clearable
                :loading="createDealProductsLoading"
                :no-filter="true"
                @update:search="handleCreateDealProductSearch"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="createDealForm.expected_close_date"
                :label="$t('ncrm.deals.board.createDeal.fields.expectedCloseDate')"
                type="date"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="createDealForm.ncrm_company_id"
                :items="createDealCompanyOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.deals.board.createDeal.fields.company')"
                clearable
                :loading="createDealCompaniesLoading"
                :no-filter="true"
                @update:search="handleCreateDealCompanySearch"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="createDealForm.ncrm_person_id"
                :items="createDealPersonOptions"
                item-title="label"
                item-value="id"
                :label="$t('ncrm.deals.board.createDeal.fields.person')"
                clearable
                :loading="createDealPeopleLoading"
                :no-filter="true"
                @update:search="handleCreateDealPersonSearch"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="createDealForm.notes"
                :label="$t('ncrm.deals.board.createDeal.fields.notes')"
                rows="2"
                auto-grow
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeCreateDealDialog">{{ $t("app.actions.cancel") }}</v-btn>
          <v-btn color="primary" :loading="createDealLoading" :disabled="createDealLoading" @click="submitCreateDeal">
            {{ $t("app.actions.create") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="boardSettingsDialog" max-width="980">
      <v-card>
        <v-card-title>{{ $t("ncrm.deals.board.settings.title") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <v-tabs v-model="boardSettingsTab" color="primary">
            <v-tab value="members">{{ $t("ncrm.deals.board.settings.tabs.members") }}</v-tab>
            <v-tab value="rules">{{ $t("ncrm.deals.board.settings.tabs.rules") }}</v-tab>
            <v-tab value="audit">{{ $t("ncrm.deals.board.settings.tabs.audit") }}</v-tab>
          </v-tabs>

          <v-window v-model="boardSettingsTab" class="mt-4">
            <v-window-item value="members">
              <div class="d-flex flex-wrap align-end ga-2 mb-3">
                <v-autocomplete
                  v-model="memberUserId"
                  :items="memberUserOptions"
                  item-title="label"
                  item-value="id"
                  :label="$t('ncrm.deals.board.members.userId')"
                  :placeholder="$t('ncrm.deals.board.members.userId')"
                  hide-details
                  clearable
                  :loading="ownerLoading"
                  class="ncrm-deal-board__member-user-field"
                />
                <v-select
                  v-model="memberRole"
                  :items="memberRoleOptions"
                  item-title="label"
                  item-value="value"
                  :label="$t('ncrm.deals.board.members.role')"
                  hide-details
                  class="ncrm-deal-board__member-role"
                />
                <v-btn color="primary" :loading="memberLoading" @click="addBoardMember">
                  <v-icon start>mdi-plus</v-icon>
                  {{ $t("ncrm.deals.board.members.add") }}
                </v-btn>
              </div>

              <v-data-table
                :headers="memberHeaders"
                :items="members"
                item-key="id"
                density="comfortable"
                class="elevation-0 ncrm-deal-board__dialog-table"
                :loading="memberLoading"
              >
                <template #item.user_name="{ item }">
                  <div class="text-body-2 ncrm-text-strong">{{ item.user_name || `#${item.user_id}` }}</div>
                  <div class="text-caption text-medium-emphasis">{{ item.user_email || "-" }}</div>
                </template>

                <template #item.role_in_board="{ item }">
                  <v-chip :color="item.role_in_board === 'owner' ? 'primary' : 'info'">
                    {{ roleLabel(item.role_in_board) }}
                  </v-chip>
                </template>

                <template #item.actions="{ item }">
                  <v-btn
                    size="small"
                    variant="text"
                    color="error"
                    :disabled="memberLoading"
                    @click="removeBoardMember(item.user_id)"
                  >
                    {{ $t("ncrm.deals.board.members.remove") }}
                  </v-btn>
                </template>

                <template #no-data>
                  <v-alert type="info" class="ma-4">
                    {{ $t("ncrm.deals.board.members.empty") }}
                  </v-alert>
                </template>
              </v-data-table>
            </v-window-item>

            <v-window-item value="rules">
              <NcrmDealBoardRulesSettings
                v-model="boardRulesForm"
                :loading="boardRulesLoading"
                :feedback="boardRulesFeedback.message"
                :feedback-color="boardRulesFeedback.color"
                @clear-feedback="clearBoardRulesFeedback"
                @save="saveBoardRules"
              />
            </v-window-item>

            <v-window-item value="audit">
              <v-row class="ncrm-deal-board__audit-filters mb-2">
                <v-col cols="12" md="4">
                  <v-select
                    v-model="boardAuditEventType"
                    :items="boardAuditEventTypeOptions"
                    item-title="label"
                    item-value="value"
                    :label="$t('ncrm.deals.board.audit.filters.eventType')"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="boardAuditChangedByUserId"
                    :items="memberUserOptions"
                    item-title="label"
                    item-value="id"
                    :label="$t('ncrm.deals.board.audit.filters.author')"
                    hide-details
                    clearable
                    :loading="ownerLoading"
                  />
                </v-col>
                <v-col cols="6" md="2">
                  <v-text-field
                    v-model="boardAuditFromDate"
                    :label="$t('ncrm.deals.board.audit.filters.fromDate')"
                    type="date"
                    hide-details="auto"
                    persistent-placeholder
                  />
                </v-col>
                <v-col cols="6" md="2">
                  <v-text-field
                    v-model="boardAuditToDate"
                    :label="$t('ncrm.deals.board.audit.filters.toDate')"
                    type="date"
                    hide-details="auto"
                    persistent-placeholder
                  />
                </v-col>
                <v-col cols="12" class="d-flex justify-end flex-wrap ga-2 ncrm-deal-board__audit-actions">
                  <v-btn size="small" variant="text" color="primary" @click="clearBoardAuditFilters">
                    {{ $t("ncrm.deals.board.audit.filters.clear") }}
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="text"
                    color="primary"
                    :loading="boardAuditLoading"
                    @click="fetchBoardAudit({ force: true, reset: true })"
                  >
                    <v-icon start size="16">mdi-refresh</v-icon>
                    {{ $t("ncrm.deals.board.audit.refresh") }}
                  </v-btn>
                </v-col>
              </v-row>

              <v-data-table
                :headers="boardAuditHeaders"
                :items="boardAuditItems"
                item-key="id"
                density="comfortable"
                class="elevation-0 ncrm-deal-board__dialog-table"
                :loading="boardAuditLoading"
              >
                <template #item.event_type="{ item }">
                  <v-chip color="primary">
                    {{ boardAuditEventLabel(item.event_type) }}
                  </v-chip>
                </template>

                <template #item.change="{ item }">
                  <span v-if="hasAuditValueChange(item)" class="text-body-2">
                    {{ auditChangeLabel(item) }}
                  </span>
                  <span v-else class="text-medium-emphasis">-</span>
                </template>

                <template #item.changed_by_name="{ item }">
                  <span class="text-body-2">{{ item.changed_by_name || `#${item.changed_by_user_id || "-"}` }}</span>
                </template>

                <template #item.created_at="{ item }">
                  <span class="text-body-2">{{ formatDateTime(item.created_at) }}</span>
                </template>

                <template #no-data>
                  <v-alert type="info" class="ma-4">
                    {{ $t("ncrm.deals.board.audit.empty") }}
                  </v-alert>
                </template>
              </v-data-table>

              <div v-if="hasMoreBoardAuditItems" class="d-flex justify-center mt-3">
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  :loading="boardAuditLoadingMore"
                  :disabled="boardAuditLoading || boardAuditLoadingMore"
                  @click="loadMoreBoardAudit"
                >
                  {{ $t("ncrm.deals.board.audit.loadMore") }}
                </v-btn>
              </div>
            </v-window-item>
          </v-window>
        </v-card-text>
        <v-card-actions class="justify-space-between">
          <v-btn
            variant="text"
            color="error"
            :disabled="deleteBoardLoading || boardRulesLoading || memberLoading || boardAuditLoading || boardAuditLoadingMore"
            @click="openDeleteBoardDialog"
          >
            <v-icon start>mdi-delete-outline</v-icon>
            {{ $t("ncrm.deals.board.delete.action") }}
          </v-btn>
          <v-btn variant="text" @click="boardSettingsDialog = false">{{ $t("app.actions.cancel") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <NcrmDealWonHandoffDialog
      v-model="wonHandoffDialog"
      :deal-id="pendingWonHandoff?.dealId"
      :deal-name="pendingWonHandoffDealName"
      :target-stage-id="pendingWonHandoff?.targetStageId"
      :select-menu-props="selectMenuProps"
      @success="handleWonHandoffSuccess"
      @cancelled="handleWonHandoffCancelled"
      @error="handleWonHandoffError"
    />

    <NcrmRecordEditDialog
      v-model="cardLayoutDialog"
      :title="$t('ncrm.leads.cardLayout.title')"
      :subtitle="$t('ncrm.leads.cardLayout.subtitle')"
      :loading="cardLayoutSaving"
      :save-disabled="cardLayoutSaving || cardLayoutLoading"
      :max-width="900"
      @save="saveCardLayoutSettings"
    >
      <v-row>
        <v-col cols="12" md="5">
          <v-select
            v-model="cardLayoutScope"
            :label="$t('ncrm.leads.cardLayout.fields.scope')"
            :items="cardLayoutScopeOptions"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
            @update:model-value="syncCardLayoutDraftByScope"
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="cardLayoutDraft.density"
            :label="$t('ncrm.leads.cardLayout.fields.density')"
            :items="densityOptions"
            item-title="label"
            item-value="value"
            :menu-props="selectMenuProps"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
          class="d-flex align-center justify-end ncrm-deal-board__reset-user-col"
        >
          <v-btn
            v-if="cardLayoutScope === 'user'"
            variant="text"
            color="primary"
            class="text-none ncrm-deal-board__reset-user-btn"
            :disabled="cardLayoutLoading || cardLayoutSaving"
            @click="resetUserCardLayout"
          >
            <v-icon start>mdi-restore</v-icon>
            {{ $t("ncrm.leads.cardLayout.actions.resetUser") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-2" />

      <div class="d-flex align-center justify-space-between ga-2 mb-2">
        <div class="text-subtitle-2 ncrm-text-strong">
          {{ $t("ncrm.deals.cardLayout.sections.deal") }}
        </div>
        <v-btn
          size="x-small"
          variant="tonal"
          color="primary"
          prepend-icon="mdi-plus"
          :disabled="cardLayoutSaving"
          @click="addCardField"
        >
          {{ $t("ncrm.leads.cardLayout.actions.addField") }}
        </v-btn>
      </div>
      <div class="ncrm-deal-board__card-fields">
        <div
          v-for="(fieldKey, index) in cardLayoutDraft.deal_fields"
          :key="`deal-card-field-${index}`"
          class="ncrm-deal-board__card-field-row"
        >
          <v-select
            :model-value="fieldKey"
            :items="cardFieldOptions"
            item-title="label"
            item-value="key"
            :menu-props="selectMenuProps"
            density="compact"
            hide-details
            @update:model-value="(value) => updateCardFieldKey(index, value)"
          />
          <v-btn
            icon="mdi-arrow-up"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="index === 0 || cardLayoutSaving"
            @click="moveCardField(index, -1)"
          />
          <v-btn
            icon="mdi-arrow-down"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="index >= cardLayoutDraft.deal_fields.length - 1 || cardLayoutSaving"
            @click="moveCardField(index, 1)"
          />
          <v-btn
            icon="mdi-delete-outline"
            size="x-small"
            variant="text"
            color="error"
            :disabled="cardLayoutDraft.deal_fields.length <= 1 || cardLayoutSaving"
            @click="removeCardField(index)"
          />
        </div>
      </div>
    </NcrmRecordEditDialog>

    <NcrmRecordDeleteDialog
      v-model="deleteBoardDialog"
      :title="$t('ncrm.deals.board.delete.title')"
      :hint="$t('ncrm.deals.board.delete.hint')"
      :confirm-label="$t('ncrm.deals.board.delete.confirmLabel')"
      :action-text="$t('ncrm.deals.board.delete.confirmAction')"
      :confirm-text="deleteBoardConfirmText"
      :loading="deleteBoardLoading"
      @update:confirm-text="deleteBoardConfirmText = $event"
      @confirm="confirmDeleteBoard"
    />

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmRecordEditDialog from "@/components/ncrm/shared/NcrmRecordEditDialog.vue";
import NcrmRecordDeleteDialog from "@/components/ncrm/shared/NcrmRecordDeleteDialog.vue";
import NcrmLastCallCell from "@/components/ncrm/shared/NcrmLastCallCell.vue";
import NcrmDealBoardToolbar from "@/components/ncrm/deals/NcrmDealBoardToolbar.vue";
import NcrmDealBoardFiltersDrawer from "@/components/ncrm/deals/NcrmDealBoardFiltersDrawer.vue";
import NcrmDealBoardRulesSettings from "@/components/ncrm/deals/NcrmDealBoardRulesSettings.vue";
import NcrmDealWonHandoffDialog from "@/components/ncrm/deals/NcrmDealWonHandoffDialog.vue";
import NcrmDealLostReasonDialog from "@/components/ncrm/deals/NcrmDealLostReasonDialog.vue";
import {
  loadPreferredView,
  resolveViewPreferencePageKey,
  savePreferredView,
} from "@/Utile/ncrmViewPreferences.mjs";
import {
  clearDealBoardFilters,
  loadDealBoardFilters,
  normalizeDealBoardFilterConfig,
  saveDealBoardFilters,
} from "@/Utile/ncrmDealBoardFilters.mjs";
import {
  buildDealBoardSearchState,
  dealBoardSearchMethods,
  dealBoardSearchWatch,
} from "@/Utile/ncrmDealBoardSearch.mjs";
import {
  activityTypeShortLabel,
  buildDealBoardTableHeaders,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  isNextActivityOverdue,
  nextActivityInlineLabel,
  shortenInlineCardValue,
  statusColor,
  statusLabel,
  toNumber,
} from "@/Utile/ncrmDealBoardDisplay.mjs";
import { resolveDealLostTaskClosureConfirmation } from "@/Utile/ncrmTerminalTaskClosure.mjs";

const DEAL_STAGE_VISIBLE_BATCH = 20;
const DEAL_CARD_DEFAULT_LAYOUT = Object.freeze({
  deal_fields: Object.freeze(["owner", "company", "status", "next_activity", "value"]),
  density: "comfortable",
});
const DEAL_DENSITY_ORDER = Object.freeze(["comfortable", "standard", "compact", "micro"]);

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const normalizeDealString = (value) => String(value || "").trim();
const normalizeDealMetaLabel = (value) =>
  normalizeDealString(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const resolveDealBillingMetaLabel = (productType, billingPeriod) => {
  const normalizedBillingPeriod = normalizeDealMetaLabel(billingPeriod);
  if (normalizedBillingPeriod) return normalizedBillingPeriod;
  const normalizedProductType = normalizeDealString(productType).toLowerCase();
  if (normalizedProductType && normalizedProductType !== "subscription") {
    return "one time";
  }
  return "";
};

const normalizeDealBoardDensity = (value, fallback = DEAL_CARD_DEFAULT_LAYOUT.density) => {
  const normalized = normalizeDealString(value).toLowerCase();
  if (DEAL_DENSITY_ORDER.includes(normalized)) return normalized;
  return DEAL_DENSITY_ORDER.includes(fallback) ? fallback : DEAL_CARD_DEFAULT_LAYOUT.density;
};

const parsePositiveId = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const normalized = Math.trunc(parsed);
  return normalized > 0 ? normalized : null;
};

const dedupeDealCardFieldKeys = (values) => {
  const source = ensureArray(values);
  const seen = new Set();
  const result = [];
  for (const entry of source) {
    const key = normalizeDealString(entry);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(key);
  }
  return result;
};

const DEAL_BOARD_VIEW_ITEMS = Object.freeze([{ value: "board" }, { value: "table" }]);
const DEAL_BOARD_PAGE_KEY_FALLBACK = "/ncrm/deals/boards";

export default {
  name: "NcrmDealBoard",
  components: {
    NcrmRecordEditDialog,
    NcrmRecordDeleteDialog,
    NcrmLastCallCell,
    NcrmDealBoardToolbar,
    NcrmDealBoardFiltersDrawer,
    NcrmDealBoardRulesSettings,
    NcrmDealWonHandoffDialog,
    NcrmDealLostReasonDialog,
  },
  props: {
    id: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    const initialBoardId = parsePositiveId(this.id || this.$route?.params?.id);
    const preferredView =
      loadPreferredView({
        pageKey: resolveViewPreferencePageKey(this.$route, DEAL_BOARD_PAGE_KEY_FALLBACK),
        items: DEAL_BOARD_VIEW_ITEMS,
        fallback: "board",
      }) || "board";
    return {
      loading: false,
      movingDealId: null,
      draggingDealId: null,
      dragSourceStageId: null,
      dragOverStageId: null,
      dragClickGuardUntil: 0,
      activeView: preferredView,
      preferredView,
      search: "",
      ...buildDealBoardSearchState(),
      advancedFiltersDialog: false,
      advancedFilters: loadDealBoardFilters({ boardId: initialBoardId }),
      board: null,
      stages: [],
      deals: [],
      ownerLoading: false,
      ownerOptions: [],
      createDealProductsLoading: false,
      createDealCompaniesLoading: false,
      createDealPeopleLoading: false,
      createDealProductOptions: [],
      createDealCompanyOptions: [],
      createDealPersonOptions: [],
      createDealProductCatalogById: {},
      createDealProductRequestSeq: 0,
      createDealCompanyRequestSeq: 0,
      createDealPersonRequestSeq: 0,
      createDealProductSearchDebounceId: null,
      createDealCompanySearchDebounceId: null,
      createDealPersonSearchDebounceId: null,
      createDealDialog: false,
      createDealLoading: false,
      createDealForm: {
        name: "",
        owner_user_id: null,
        stage_id: null,
        product_ids: [],
        expected_close_date: "",
        ncrm_company_id: null,
        ncrm_person_id: null,
        notes: "",
      },
      boardSettingsDialog: false,
      boardSettingsTab: "members",
      restoreBoardSettingsAfterDeleteDialog: false,
      deleteBoardDialog: false,
      deleteBoardConfirmText: "",
      deleteBoardLoading: false,
      deleteDialogOpenTimerId: null,
      cardLayoutDialog: false,
      cardLayoutLoading: false,
      cardLayoutSaving: false,
      cardLayoutScope: "user",
      cardLayoutConfig: {
        available_fields: [],
        default_layout: {
          deal_fields: [...DEAL_CARD_DEFAULT_LAYOUT.deal_fields],
          density: DEAL_CARD_DEFAULT_LAYOUT.density,
        },
        board_layout: null,
        user_layout: null,
        effective_layout: {
          deal_fields: [...DEAL_CARD_DEFAULT_LAYOUT.deal_fields],
          density: DEAL_CARD_DEFAULT_LAYOUT.density,
        },
      },
      cardLayoutDraft: {
        deal_fields: [...DEAL_CARD_DEFAULT_LAYOUT.deal_fields],
        density: DEAL_CARD_DEFAULT_LAYOUT.density,
      },
      effectiveDensity: DEAL_CARD_DEFAULT_LAYOUT.density,
      boardRulesLoading: false,
      boardRulesForm: {
        autoCreateTaskIfMissingAfterChange: false,
        autoCreateTaskIfMissingProduct: false,
        autoCreateTaskIfMissingDelayMinutes: 60,
      },
      boardRulesFeedback: {
        message: "",
        color: "success",
      },
      memberLoading: false,
      memberUserId: null,
      memberRole: "member",
      members: [],
      boardAuditLoading: false,
      boardAuditLoadingMore: false,
      boardAuditItems: [],
      boardAuditTotal: 0,
      boardAuditPage: 0,
      boardAuditPageSize: 50,
      boardAuditEventType: "all",
      boardAuditChangedByUserId: null,
      boardAuditFromDate: "",
      boardAuditToDate: "",
      boardAuditSuppressFilterReload: false,
      lostReasonDialog: false,
      pendingLostStageMove: null,
      wonHandoffDialog: false,
      pendingWonHandoff: null,
      stageVisibleCounts: {},
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    boardId() {
      return Number(this.id || this.$route?.params?.id || 0) || null;
    },
    viewItems() {
      return [{ value: "board", label: this.$t("ncrm.deals.views.board") }, { value: "table", label: this.$t("ncrm.deals.views.table") }];
    },
    boardColumns() {
      const hasSearch = Boolean(String(this.search || "").trim());
      return (this.stages || []).map((stage) => {
        const stageId = Number(stage?.id || 0) || null;
        const items = (Array.isArray(this.deals) ? this.deals : []).filter((deal) => Number(deal?.stage_id || 0) === stageId);
        const visibleLimit = hasSearch ? items.length : this.resolveStageVisibleCount(stageId);
        const visibleItems = items.slice(0, visibleLimit);
        return {
          stage_id: stageId || 0,
          stage_name: stage?.name || "-",
          stage_key: String(stage?.stage_key || "").trim(),
          stage_type: String(stage?.stage_type || "").trim(),
          items,
          visible_items: visibleItems,
          visible_count: visibleItems.length,
          remaining_count: Math.max(items.length - visibleItems.length, 0),
          has_more: items.length > visibleItems.length,
        };
      });
    },
    stageOptions() {
      return (this.stages || []).map((stage) => ({
        id: Number(stage?.id || 0),
        label: stage?.name || `#${stage?.id}`,
      }));
    },
    tableHeaders() {
      return buildDealBoardTableHeaders((key) => this.$t(key));
    },
    densityOptions() {
      return [
        { value: "comfortable", label: this.$t("ncrm.leads.board.density.comfortable") },
        { value: "standard", label: this.$t("ncrm.leads.board.density.standard") },
        { value: "compact", label: this.$t("ncrm.leads.board.density.compact") },
        { value: "micro", label: this.$t("ncrm.leads.board.density.micro") },
      ];
    },
    selectMenuProps() {
      return {
        maxHeight: 320,
        contentClass: "ncrm-deal-board-select-menu",
        zIndex: 2710,
      };
    },
    cardLayoutScopeOptions() {
      return [
        { value: "user", label: this.$t("ncrm.leads.cardLayout.scope.user") },
        { value: "board", label: this.$t("ncrm.leads.cardLayout.scope.board") },
      ];
    },
    cardFieldOptions() {
      return ensureArray(this.cardLayoutConfig?.available_fields)
        .map((entry) => ({
          key: normalizeDealString(entry?.key),
          label: normalizeDealString(entry?.label) || normalizeDealString(entry?.key),
        }))
        .filter((entry) => entry.key);
    },
    effectiveDealCardLayout() {
      const fallback = {
        deal_fields: [...DEAL_CARD_DEFAULT_LAYOUT.deal_fields],
        density: DEAL_CARD_DEFAULT_LAYOUT.density,
      };
      const source = this.cardLayoutConfig?.effective_layout || fallback;
      return {
        deal_fields: dedupeDealCardFieldKeys(source?.deal_fields || source?.dealFields || fallback.deal_fields),
        density: normalizeDealBoardDensity(source?.density, fallback.density),
      };
    },
    dealCardPaddingClass() {
      if (this.effectiveDensity === "micro") return "pa-1";
      if (this.effectiveDensity === "compact") return "pa-1";
      if (this.effectiveDensity === "standard") return "pa-2";
      return "pa-2";
    },
    memberHeaders() {
      return [
        { title: this.$t("ncrm.deals.board.members.headers.user"), key: "user_name" },
        { title: this.$t("ncrm.deals.board.members.headers.role"), key: "role_in_board" },
        { title: this.$t("ncrm.deals.board.members.headers.addedAt"), key: "created_at" },
        { title: this.$t("ncrm.deals.board.members.headers.actions"), key: "actions", sortable: false },
      ];
    },
    boardAuditHeaders() {
      return [
        { title: this.$t("ncrm.deals.board.audit.headers.eventType"), key: "event_type", sortable: false },
        { title: this.$t("ncrm.deals.board.audit.headers.change"), key: "change", sortable: false },
        { title: this.$t("ncrm.deals.board.audit.headers.author"), key: "changed_by_name", sortable: false },
        { title: this.$t("ncrm.deals.board.audit.headers.createdAt"), key: "created_at", sortable: false },
      ];
    },
    boardAuditEventTypeOptions() {
      return [
        { value: "all", label: this.$t("ncrm.deals.board.audit.filters.allEvents") },
        { value: "deal_board_created", label: this.boardAuditEventLabel("deal_board_created") },
        { value: "deal_board_updated", label: this.boardAuditEventLabel("deal_board_updated") },
        { value: "deal_board_member_added", label: this.boardAuditEventLabel("deal_board_member_added") },
        { value: "deal_board_member_removed", label: this.boardAuditEventLabel("deal_board_member_removed") },
        { value: "deal_stage_added", label: this.boardAuditEventLabel("deal_stage_added") },
        { value: "deal_stage_updated", label: this.boardAuditEventLabel("deal_stage_updated") },
        { value: "deal_stage_removed", label: this.boardAuditEventLabel("deal_stage_removed") },
        { value: "deal_stage_reordered", label: this.boardAuditEventLabel("deal_stage_reordered") },
      ];
    },
    hasMoreBoardAuditItems() {
      return this.boardAuditTotal > this.boardAuditItems.length;
    },
    memberRoleOptions() {
      return [
        { value: "member", label: this.roleLabel("member") },
        { value: "owner", label: this.roleLabel("owner") },
      ];
    },
    memberUserOptions() {
      return (this.ownerOptions || []).map((item) => ({
        id: Number(item?.id || 0),
        label: item?.label || `#${item?.id}`,
      }));
    },
    boardAuditUserLabelMap() {
      const map = new Map();
      for (const option of this.ownerOptions || []) {
        const id = Number(option?.id || 0) || null;
        if (!id) continue;
        const label = String(option?.label || "").trim() || `#${id}`;
        map.set(id, label);
      }
      for (const member of this.members || []) {
        const id = Number(member?.user_id || 0) || null;
        if (!id) continue;
        const label =
          String(member?.user_name || "").trim() ||
          String(member?.user_email || "").trim() ||
          `#${id}`;
        map.set(id, label);
      }
      for (const row of this.boardAuditItems || []) {
        const payload = row?.payload_json && typeof row.payload_json === "object" ? row.payload_json : null;
        const id = Number(payload?.user_id || 0) || null;
        if (!id) continue;
        const label =
          String(payload?.user_name || "").trim() ||
          String(payload?.user_display_name || "").trim() ||
          String(payload?.user_email || "").trim();
        if (label) {
          map.set(id, label);
        }
      }
      return map;
    },
    boardAuditStageNameMap() {
      const map = new Map();
      for (const stage of this.stages || []) {
        const id = Number(stage?.id || 0) || null;
        if (!id) continue;
        const label = String(stage?.name || "").trim() || `#${id}`;
        map.set(id, label);
      }
      return map;
    },
    pendingWonHandoffDealName() {
      const dealId = parsePositiveId(this.pendingWonHandoff?.dealId);
      if (!dealId) return "";
      const deal = (this.deals || []).find((item) => parsePositiveId(item?.id) === dealId);
      return normalizeDealString(deal?.name);
    },
    densityLabelMap() {
      return this.densityOptions.reduce((acc, option) => {
        if (!option?.value) return acc;
        acc[option.value] = option.label;
        return acc;
      }, {});
    },
    currentDensityLabel() {
      return this.densityLabelMap[this.effectiveDensity] || this.densityLabelMap.comfortable || "Comfortable";
    },
    nextDensity() {
      const currentIndex = DEAL_DENSITY_ORDER.indexOf(this.effectiveDensity);
      const startIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex = (startIndex + 1) % DEAL_DENSITY_ORDER.length;
      return DEAL_DENSITY_ORDER[nextIndex];
    },
    nextDensityLabel() {
      return this.densityLabelMap[this.nextDensity] || this.nextDensity;
    },
    densityStateIcon() {
      const iconMap = {
        comfortable: "mdi-view-day-outline",
        standard: "mdi-view-agenda-outline",
        compact: "mdi-view-sequential-outline",
        micro: "mdi-view-list-outline",
      };
      return iconMap[this.effectiveDensity] || iconMap.comfortable;
    },
    densityCycleTooltip() {
      return this.$t("ncrm.leads.board.densityCycleTooltip", {
        current: this.currentDensityLabel,
        next: this.nextDensityLabel,
      });
    },
    densityCycleAriaLabel() {
      return this.$t("ncrm.leads.board.densityCycleAria", {
        current: this.currentDensityLabel,
        next: this.nextDensityLabel,
      });
    },
    densityCycleDisabled() {
      return Boolean(this.cardLayoutLoading || this.cardLayoutSaving || !this.boardId);
    },
  },
  watch: {
    boardId() {
      this.restorePreferredView();
      this.restoreBoardFilters();
      this.loadBoard();
    },
    ...dealBoardSearchWatch,
    boardAuditEventType() {
      this.handleBoardAuditFilterChange();
    },
    boardAuditChangedByUserId() {
      this.handleBoardAuditFilterChange();
    },
    boardAuditFromDate() {
      this.handleBoardAuditFilterChange();
    },
    boardAuditToDate() {
      this.handleBoardAuditFilterChange();
    },
    deleteBoardDialog(value) {
      if (value || this.deleteBoardLoading) return;
      if (!this.restoreBoardSettingsAfterDeleteDialog) return;
      this.restoreBoardSettingsAfterDeleteDialog = false;
      this.boardSettingsDialog = true;
    },
  },
  mounted() {
    this.loadBoard();
  },
  beforeUnmount() {
    if (this.searchDebounceId) clearTimeout(this.searchDebounceId);
    if (this.deleteDialogOpenTimerId) clearTimeout(this.deleteDialogOpenTimerId);
    if (this.createDealProductSearchDebounceId) clearTimeout(this.createDealProductSearchDebounceId);
    if (this.createDealCompanySearchDebounceId) clearTimeout(this.createDealCompanySearchDebounceId);
    if (this.createDealPersonSearchDebounceId) clearTimeout(this.createDealPersonSearchDebounceId);
  },
  methods: {
    viewPreferencePageKey() {
      return resolveViewPreferencePageKey(this.$route, DEAL_BOARD_PAGE_KEY_FALLBACK);
    },
    restoreBoardFilters() {
      this.advancedFiltersDialog = false;
      this.advancedFilters = loadDealBoardFilters({ boardId: this.boardId });
    },
    persistBoardFilters(filters) {
      const normalized = saveDealBoardFilters({
        boardId: this.boardId,
        filters: normalizeDealBoardFilterConfig(filters),
      });
      this.advancedFilters = normalized;
      return normalized;
    },
    async clearAdvancedFilters({ reload = true } = {}) {
      clearDealBoardFilters({ boardId: this.boardId });
      this.advancedFilters = normalizeDealBoardFilterConfig();
      if (reload) {
        await this.fetchDeals().catch((error) => {
          console.error("[ncrm-deal-board] clear advanced filters error", error);
          this.showSnackbar(
            error?.response?.data?.error || this.$t("ncrm.deals.board.messages.loadError"),
            "error"
          );
        });
      }
    },
    async applyAdvancedFilters(filters) {
      this.persistBoardFilters(filters);
      await this.fetchDeals().catch((error) => {
        console.error("[ncrm-deal-board] apply advanced filters error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.loadError"),
          "error"
        );
      });
    },
    async removeAdvancedFilter(index) {
      const nextRules = (Array.isArray(this.advancedFilters?.rules) ? this.advancedFilters.rules : []).filter(
        (_rule, ruleIndex) => ruleIndex !== index
      );
      if (!nextRules.length) {
        await this.clearAdvancedFilters({ reload: true });
        return;
      }
      this.persistBoardFilters({
        logic: this.advancedFilters?.logic || "and",
        rules: nextRules,
      });
      await this.fetchDeals().catch((error) => {
        console.error("[ncrm-deal-board] remove advanced filter error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.loadError"),
          "error"
        );
      });
    },
    async resetFilters() {
      if (this.searchDebounceId) {
        clearTimeout(this.searchDebounceId);
        this.searchDebounceId = null;
      }
      this.search = "";
      clearDealBoardFilters({ boardId: this.boardId });
      this.advancedFilters = normalizeDealBoardFilterConfig();
      await this.fetchDeals().catch((error) => {
        console.error("[ncrm-deal-board] reset filters error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.loadError"),
          "error"
        );
      });
    },
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    restorePreferredView() {
      this.preferredView =
        loadPreferredView({
          pageKey: this.viewPreferencePageKey(),
          items: DEAL_BOARD_VIEW_ITEMS,
          fallback: "board",
        }) || "board";
      this.activeView = this.preferredView;
    },
    setPreferredView(value) {
      const preferredView = savePreferredView({
        pageKey: this.viewPreferencePageKey(),
        value,
        items: DEAL_BOARD_VIEW_ITEMS,
      });
      if (!preferredView) return;
      this.preferredView = preferredView;
      if (preferredView !== this.activeView) {
        this.activeView = preferredView;
      }
    },
    formatNumber,
    formatCurrency,
    formatDate,
    formatDateTime,
    isNextActivityOverdue,
    resolveStageVisibleCount(stageId) {
      const normalizedStageId = Number(stageId || 0) || null;
      if (!normalizedStageId) return DEAL_STAGE_VISIBLE_BATCH;
      const current = Number(this.stageVisibleCounts?.[normalizedStageId] || 0);
      if (!Number.isFinite(current) || current <= 0) return DEAL_STAGE_VISIBLE_BATCH;
      return Math.trunc(current);
    },
    stageCountLabel(column = {}) {
      const total = Array.isArray(column?.items) ? column.items.length : 0;
      const shown = Number(column?.visible_count || 0);
      if (total > shown) {
        return this.$t("ncrm.deals.board.showingCount", { shown, total });
      }
      return String(total);
    },
    stageLoadMoreLabel(column = {}) {
      const remaining = Number(column?.remaining_count || 0);
      const nextBatch = Math.min(remaining, DEAL_STAGE_VISIBLE_BATCH);
      return this.$t("ncrm.deals.board.loadMore", { count: nextBatch });
    },
    loadMoreStageDeals(stageId) {
      const normalizedStageId = Number(stageId || 0) || null;
      if (!normalizedStageId) return;
      const current = this.resolveStageVisibleCount(normalizedStageId);
      this.stageVisibleCounts = {
        ...this.stageVisibleCounts,
        [normalizedStageId]: current + DEAL_STAGE_VISIBLE_BATCH,
      };
    },
    syncStageVisibleCounts(stages = []) {
      const nextMap = {};
      for (const stage of stages || []) {
        const stageId = Number(stage?.id || stage?.stage_id || 0) || null;
        if (!stageId) continue;
        nextMap[stageId] = this.resolveStageVisibleCount(stageId);
      }
      this.stageVisibleCounts = nextMap;
    },
    isDealDraggable(deal = {}) {
      const dealId = parsePositiveId(deal?.id);
      if (!dealId) return false;
      if (this.loading) return false;
      if (this.movingDealId) return false;
      return true;
    },
    onDealDragStart(event, deal = {}, column = {}) {
      if (!this.isDealDraggable(deal)) {
        event?.preventDefault?.();
        return;
      }
      const dealId = parsePositiveId(deal?.id);
      const sourceStageId = parsePositiveId(deal?.stage_id ?? column?.stage_id);
      if (!dealId || !sourceStageId) {
        event?.preventDefault?.();
        return;
      }
      this.draggingDealId = dealId;
      this.dragSourceStageId = sourceStageId;
      this.dragOverStageId = sourceStageId;
      this.dragClickGuardUntil = Date.now() + 350;
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.dropEffect = "move";
        event.dataTransfer.setData("text/plain", String(dealId));
      }
    },
    onDealDragEnd() {
      this.dragClickGuardUntil = Date.now() + 350;
      this.clearDragState();
    },
    clearDragState() {
      this.draggingDealId = null;
      this.dragSourceStageId = null;
      this.dragOverStageId = null;
    },
    columnDragClasses(column = {}) {
      const stageId = parsePositiveId(column?.stage_id);
      if (!stageId || !this.draggingDealId) return {};
      if (stageId === this.dragSourceStageId) {
        return { "ncrm-deal-board__column--drag-source": true };
      }
      if (stageId === this.dragOverStageId) {
        return { "ncrm-deal-board__column--drop-target": true };
      }
      return {};
    },
    onStageDragOver(event, column = {}) {
      if (!this.draggingDealId || this.movingDealId) return;
      const stageId = parsePositiveId(column?.stage_id);
      if (!stageId) return;
      this.dragOverStageId = stageId;
      if (event?.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
    },
    async onStageDrop(column = {}) {
      if (!this.draggingDealId || this.movingDealId) {
        this.clearDragState();
        return;
      }
      const targetStageId = parsePositiveId(column?.stage_id);
      const dealId = parsePositiveId(this.draggingDealId);
      const sourceStageId = parsePositiveId(this.dragSourceStageId);
      if (!dealId || !targetStageId) {
        this.clearDragState();
        return;
      }
      if (sourceStageId === targetStageId) {
        this.clearDragState();
        return;
      }
      await this.requestDealMoveToStage({ dealId, targetStageId });
      this.clearDragState();
    },
    statusLabel,
    statusColor,
    activityTypeShortLabel,
    shortenInlineCardValue,
    nextActivityInlineLabel,
    resolveDealCardLayoutOrFallback(layout) {
      const fallback = {
        deal_fields: [...DEAL_CARD_DEFAULT_LAYOUT.deal_fields],
        density: DEAL_CARD_DEFAULT_LAYOUT.density,
      };
      if (!layout || typeof layout !== "object") return fallback;
      return {
        deal_fields: dedupeDealCardFieldKeys(layout?.deal_fields || layout?.dealFields || fallback.deal_fields),
        density: normalizeDealBoardDensity(layout?.density, fallback.density),
      };
    },
    syncCardLayoutDraftByScope() {
      const source = this.cardLayoutScope === "board"
        ? this.resolveDealCardLayoutOrFallback(this.cardLayoutConfig?.board_layout || this.cardLayoutConfig?.default_layout)
        : this.resolveDealCardLayoutOrFallback(this.cardLayoutConfig?.user_layout || this.cardLayoutConfig?.effective_layout);
      this.cardLayoutDraft = {
        deal_fields: [...source.deal_fields],
        density: source.density,
      };
    },
    async openCardLayoutDialog() {
      await this.loadCardLayoutConfig();
      this.cardLayoutDialog = true;
      this.syncCardLayoutDraftByScope();
    },
    async loadCardLayoutConfig() {
      if (!this.boardId) return;
      this.cardLayoutLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/deals/boards/${this.boardId}/card-layout`);
        const safeData = data || {};
        this.cardLayoutConfig = {
          available_fields: Array.isArray(safeData?.available_fields) ? safeData.available_fields : [],
          default_layout: this.resolveDealCardLayoutOrFallback(safeData?.default_layout),
          board_layout: safeData?.board_layout ? this.resolveDealCardLayoutOrFallback(safeData.board_layout) : null,
          user_layout: safeData?.user_layout ? this.resolveDealCardLayoutOrFallback(safeData.user_layout) : null,
          effective_layout: this.resolveDealCardLayoutOrFallback(safeData?.effective_layout),
        };
        this.effectiveDensity = normalizeDealBoardDensity(
          this.cardLayoutConfig?.effective_layout?.density,
          DEAL_CARD_DEFAULT_LAYOUT.density
        );
      } catch (error) {
        console.error("[ncrm-deal-board] load card layout error", error);
        this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.loadError"), "error");
      } finally {
        this.cardLayoutLoading = false;
      }
    },
    addCardField() {
      const options = this.cardFieldOptions || [];
      const current = dedupeDealCardFieldKeys(this.cardLayoutDraft?.deal_fields);
      const nextAvailableKey =
        options.find((entry) => entry?.key && !current.includes(entry.key))?.key || options[0]?.key;
      if (!nextAvailableKey) return;
      current.push(nextAvailableKey);
      this.cardLayoutDraft.deal_fields = dedupeDealCardFieldKeys(current);
    },
    removeCardField(index) {
      const current = dedupeDealCardFieldKeys(this.cardLayoutDraft?.deal_fields);
      if (current.length <= 1) return;
      current.splice(index, 1);
      this.cardLayoutDraft.deal_fields = dedupeDealCardFieldKeys(current);
    },
    moveCardField(index, delta) {
      const current = dedupeDealCardFieldKeys(this.cardLayoutDraft?.deal_fields);
      const targetIndex = Number(index) + Number(delta || 0);
      if (targetIndex < 0 || targetIndex >= current.length) return;
      const [item] = current.splice(index, 1);
      current.splice(targetIndex, 0, item);
      this.cardLayoutDraft.deal_fields = dedupeDealCardFieldKeys(current);
    },
    updateCardFieldKey(index, value) {
      const key = normalizeDealString(value);
      if (!key) return;
      const current = dedupeDealCardFieldKeys(this.cardLayoutDraft?.deal_fields);
      if (index < 0 || index >= current.length) return;
      current.splice(index, 1, key);
      this.cardLayoutDraft.deal_fields = dedupeDealCardFieldKeys(current);
    },
    async saveCardLayoutSettings() {
      if (!this.boardId) return;
      this.cardLayoutSaving = true;
      try {
        const payload = {
          density: normalizeDealBoardDensity(this.cardLayoutDraft?.density, DEAL_CARD_DEFAULT_LAYOUT.density),
          deal_fields: dedupeDealCardFieldKeys(this.cardLayoutDraft?.deal_fields),
        };
        if (!payload.deal_fields.length) {
          this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.fieldRequired"), "error");
          return;
        }

        if (this.cardLayoutScope === "board") {
          await axios.put(`/ncrm/deals/boards/${this.boardId}/card-layout`, payload);
        } else {
          await axios.put(`/ncrm/deals/boards/${this.boardId}/card-layout/user`, payload);
        }
        await this.loadCardLayoutConfig();
        this.cardLayoutDialog = false;
        this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.saveSuccess"), "success");
      } catch (error) {
        console.error("[ncrm-deal-board] save card layout error", error);
        const apiMessage = error?.response?.data?.error;
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.cardLayout.messages.saveError"), "error");
      } finally {
        this.cardLayoutSaving = false;
      }
    },
    async resetUserCardLayout() {
      if (!this.boardId) return;
      this.cardLayoutSaving = true;
      try {
        await axios.delete(`/ncrm/deals/boards/${this.boardId}/card-layout/user`);
        await this.loadCardLayoutConfig();
        this.syncCardLayoutDraftByScope();
        this.showSnackbar(this.$t("ncrm.leads.cardLayout.messages.resetSuccess"), "success");
      } catch (error) {
        console.error("[ncrm-deal-board] reset user card layout error", error);
        const apiMessage = error?.response?.data?.error;
        this.showSnackbar(apiMessage || this.$t("ncrm.leads.cardLayout.messages.resetError"), "error");
      } finally {
        this.cardLayoutSaving = false;
      }
    },
    dealCardFieldLabel(fieldKey) {
      const key = normalizeDealString(fieldKey).toLowerCase();
      const map = {
        owner: this.$t("ncrm.deals.board.owner"),
        company: this.$t("ncrm.deals.board.company"),
        person: this.$t("ncrm.deals.details.context.person"),
        status: this.$t("ncrm.deals.board.table.status"),
        next_activity: this.$t("ncrm.deals.board.nextActivity"),
        value: this.$t("ncrm.deals.board.value"),
        expected_close_date: this.$t("ncrm.deals.board.table.expectedCloseDate"),
        updated_at: this.$t("ncrm.deals.board.table.updatedAt"),
        stage: this.$t("ncrm.deals.board.table.stage"),
        open_activity_count: this.$t("ncrm.deals.details.context.openActivities"),
      };
      return map[key] || key;
    },
    buildDealCardField(deal, fieldKey) {
      const key = normalizeDealString(fieldKey).toLowerCase();
      if (!key) return null;
      const label = this.dealCardFieldLabel(key);
      if (key === "owner") {
        return { key, label, value: normalizeDealString(deal?.owner_name) || "-" };
      }
      if (key === "company") {
        return { key, label, value: normalizeDealString(deal?.company_name) || "-" };
      }
      if (key === "person") {
        return { key, label, value: normalizeDealString(deal?.person_name) || "-" };
      }
      if (key === "status") {
        return {
          key,
          label,
          value: this.statusLabel(deal?.status),
          variant: "chip",
          color: this.statusColor(deal?.status),
        };
      }
      if (key === "next_activity") {
        return {
          key,
          label,
          value: this.nextActivityInlineLabel(deal),
          overdue: this.isNextActivityOverdue(deal?.next_activity_due_at),
        };
      }
      if (key === "value") {
        return { key, label, value: this.formatCurrency(deal?.expected_value) };
      }
      if (key === "expected_close_date") {
        return { key, label, value: this.formatDate(deal?.expected_close_date) };
      }
      if (key === "updated_at") {
        return { key, label, value: this.formatDateTime(deal?.updated_at) };
      }
      if (key === "stage") {
        return {
          key,
          label,
          value: normalizeDealString(deal?.stage_name) || "-",
          variant: "chip",
          color: "secondary",
        };
      }
      if (key === "open_activity_count") {
        return {
          key,
          label,
          value: this.formatNumber(deal?.open_activity_count ?? 0),
        };
      }
      return null;
    },
    resolvedDealCardFields(deal) {
      const keys = dedupeDealCardFieldKeys(this.effectiveDealCardLayout?.deal_fields);
      const result = [];
      for (const key of keys) {
        const field = this.buildDealCardField(deal, key);
        if (field) result.push(field);
      }
      return result;
    },
    isClosedStageType(stageType) {
      const normalized = String(stageType || "").trim().toLowerCase();
      return normalized === "won" || normalized === "lost";
    },
    resolveBoardAutoMissingTaskRuleValue(board = this.board) {
      return Number(board?.auto_create_task_if_missing_after_change) === 1 ||
        board?.auto_create_task_if_missing_after_change === true;
    },
    resolveBoardAutoMissingProductRuleValue(board = this.board) {
      return Number(board?.auto_create_task_if_missing_product) === 1 ||
        board?.auto_create_task_if_missing_product === true;
    },
    resolveBoardAutoMissingTaskDelayMinutes(board = this.board) {
      const parsed = Number(
        board?.auto_create_task_if_missing_delay_minutes ?? board?.autoCreateTaskIfMissingDelayMinutes
      );
      if (!Number.isFinite(parsed)) return 60;
      const normalized = Math.trunc(parsed);
      if (normalized < 1) return 1;
      if (normalized > 10080) return 10080;
      return normalized;
    },
    initializeBoardRulesFromBoard(board = this.board) {
      this.boardRulesForm = {
        autoCreateTaskIfMissingAfterChange: this.resolveBoardAutoMissingTaskRuleValue(board),
        autoCreateTaskIfMissingProduct: this.resolveBoardAutoMissingProductRuleValue(board),
        autoCreateTaskIfMissingDelayMinutes: this.resolveBoardAutoMissingTaskDelayMinutes(board),
      };
    },
    clearBoardRulesFeedback() {
      this.boardRulesFeedback = {
        message: "",
        color: "success",
      };
    },
    setBoardRulesFeedback(message, color = "success") {
      this.boardRulesFeedback = {
        message,
        color,
      };
    },
    roleLabel(role) {
      const normalized = String(role || "").trim().toLowerCase();
      return this.$t(`ncrm.deals.board.members.roles.${normalized}`) || normalized || "-";
    },
    boardAuditEventLabel(eventType) {
      const normalized = String(eventType || "").trim().toLowerCase().replace(/\s+/g, "_");
      if (!normalized) return this.$t("ncrm.contacts.details.history.eventTypes.updated");
      const key = `ncrm.contacts.details.history.eventTypes.${normalized}`;
      if (typeof this.$te === "function" && this.$te(key)) {
        return this.$t(key);
      }
      return normalized;
    },
    hasAuditValueChange(item = {}) {
      return Boolean(String(item?.from_value || "").trim() || String(item?.to_value || "").trim());
    },
    parseAuditNumericId(value) {
      const normalized = String(value ?? "").trim();
      if (!/^\d+$/.test(normalized)) return null;
      const parsed = Number(normalized);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return parsed;
    },
    auditUserLabel(value, item = {}) {
      const normalized = String(value ?? "").trim();
      if (!normalized) return "-";
      const userId = this.parseAuditNumericId(normalized);
      if (!userId) return normalized;

      const payload = item?.payload_json && typeof item.payload_json === "object" ? item.payload_json : null;
      const payloadName =
        Number(payload?.user_id || 0) === userId
          ? String(payload?.user_name || payload?.user_display_name || payload?.user_email || "").trim()
          : "";

      return (
        payloadName ||
        this.boardAuditUserLabelMap.get(userId) ||
        this.$t("ncrm.deals.board.audit.changeTemplates.userFallback", { id: userId })
      );
    },
    auditStageLabel(value) {
      const normalized = String(value ?? "").trim();
      if (!normalized) return "-";
      const stageId = this.parseAuditNumericId(normalized);
      if (!stageId) return normalized;
      return this.boardAuditStageNameMap.get(stageId) || `#${stageId}`;
    },
    auditChangeLabel(item = {}) {
      const eventType = String(item?.event_type || "").trim().toLowerCase();
      if (!this.hasAuditValueChange(item)) return "-";

      const fromRaw = String(item?.from_value ?? "").trim();
      const toRaw = String(item?.to_value ?? "").trim();

      let fromLabel = fromRaw || "-";
      let toLabel = toRaw || "-";

      if (eventType.includes("member")) {
        fromLabel = this.auditUserLabel(fromRaw, item);
        toLabel = this.auditUserLabel(toRaw, item);
      } else if (eventType.includes("stage")) {
        fromLabel = this.auditStageLabel(fromRaw);
        toLabel = this.auditStageLabel(toRaw);
      }

      if (eventType === "deal_board_member_added") {
        return this.$t("ncrm.deals.board.audit.changeTemplates.memberAdded", { user: toLabel });
      }
      if (eventType === "deal_board_member_removed") {
        return this.$t("ncrm.deals.board.audit.changeTemplates.memberRemoved", { user: fromLabel });
      }
      if (!fromRaw && toRaw) {
        return this.$t("ncrm.deals.board.audit.changeTemplates.setTo", { value: toLabel });
      }
      if (fromRaw && !toRaw) {
        return this.$t("ncrm.deals.board.audit.changeTemplates.removed", { value: fromLabel });
      }
      return `${fromLabel} → ${toLabel}`;
    },
    openDeal(dealId) {
      if (!dealId) return;
      this.$router.push({
        name: "NcrmDealDetails",
        params: { id: String(dealId) },
      });
    },
    handleDealCardActivate(dealId) {
      const normalizedDealId = parsePositiveId(dealId);
      if (!normalizedDealId) return;
      if (this.movingDealId || this.draggingDealId) return;
      if (Date.now() < Number(this.dragClickGuardUntil || 0)) return;
      this.openDeal(normalizedDealId);
    },
    handleTableRowClick(_event, payload) {
      const row = payload?.item?.raw ?? payload?.item ?? payload;
      this.openDeal(Number(row?.id || 0));
    },
    async fetchBoard() {
      if (!this.boardId) return;
      const { data } = await axios.get(`/ncrm/deals/boards/${this.boardId}`);
      const board = data?.board || null;
      this.board = board;
      this.stages = Array.isArray(board?.stages) ? board.stages : [];
      this.syncStageVisibleCounts(this.stages);
      this.initializeBoardRulesFromBoard(board);
    },
    ...dealBoardSearchMethods,
    async fetchOwners() {
      this.ownerLoading = true;
      try {
        let data = null;
        try {
          const response = await axios.get("/ncrm/deals/owners", {
            params: {
              limit: 500,
            },
          });
          data = response?.data || null;
        } catch (primaryError) {
          const statusCode = Number(primaryError?.response?.status || 0);
          // Compat fallback: backend vechi fără endpoint dedicat pentru deals owners.
          if (statusCode === 400 || statusCode === 404) {
            const fallbackResponse = await axios.get("/ncrm/leads/owners", {
              params: {
                limit: 500,
              },
            });
            data = fallbackResponse?.data || null;
          } else {
            throw primaryError;
          }
        }
        this.ownerOptions = (Array.isArray(data?.items) ? data.items : []).map((item) => ({
          id: Number(item?.id || 0),
          label: item?.display_name || item?.name || item?.email || `#${item?.id}`,
        }));
      } catch (error) {
        console.error("[ncrm-deal-board] load owners error", error);
        this.ownerOptions = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.loadError"),
          "error"
        );
      } finally {
        this.ownerLoading = false;
      }
      return this.ownerOptions;
    },
    async loadBoard() {
      if (!this.boardId) return;
      this.loading = true;
      this.clearDragState();
      try {
        await Promise.all([this.fetchBoard(), this.fetchDeals(), this.loadCardLayoutConfig()]);
      } catch (error) {
        console.error("[ncrm-deal-board] load error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.loadError"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    canMoveDeal(deal, offset) {
      const currentStageId = Number(deal?.stage_id || 0);
      if (!currentStageId) return false;
      const currentIndex = this.stages.findIndex((stage) => Number(stage?.id || 0) === currentStageId);
      if (currentIndex < 0) return false;
      const nextIndex = currentIndex + Number(offset || 0);
      return nextIndex >= 0 && nextIndex < this.stages.length;
    },
    async moveDealByOffset(deal, offset) {
      if (!this.canMoveDeal(deal, offset)) return;
      const currentStageId = Number(deal?.stage_id || 0);
      const currentIndex = this.stages.findIndex((stage) => Number(stage?.id || 0) === currentStageId);
      const targetStage = this.stages[currentIndex + Number(offset || 0)];
      const targetStageId = Number(targetStage?.id || 0);
      if (!targetStageId) return;
      await this.requestDealMoveToStage({
        dealId: Number(deal?.id || 0) || null,
        targetStageId,
      });
    },
    resolveStageById(stageId) {
      const normalizedStageId = parsePositiveId(stageId);
      if (!normalizedStageId) return null;
      return (this.stages || []).find((stage) => parsePositiveId(stage?.id) === normalizedStageId) || null;
    },
    resolveDealNameById(dealId) {
      const normalizedDealId = parsePositiveId(dealId);
      if (!normalizedDealId) return "";
      const deal = (this.deals || []).find((entry) => parsePositiveId(entry?.id) === normalizedDealId);
      return String(deal?.name || "").trim();
    },
    async openWonHandoffDialog({ dealId, targetStageId } = {}) {
      const normalizedDealId = parsePositiveId(dealId);
      if (!normalizedDealId) return;
      this.pendingWonHandoff = {
        dealId: normalizedDealId,
        targetStageId: parsePositiveId(targetStageId),
      };
      this.wonHandoffDialog = true;
    },
    handleWonHandoffCancelled() {
      this.pendingWonHandoff = null;
    },
    async handleWonHandoffSuccess(payload = {}) {
      this.pendingWonHandoff = null;
      const contractId = parsePositiveId(payload?.response?.data?.contract?.id);
      if (contractId) {
        this.$router.push({
          name: "NcrmContractDetails",
          params: { contractId: String(contractId) },
          query: {
            tab: "billing",
            billingSetup: "1",
          },
        });
        return;
      }
      this.showSnackbar(this.$t("ncrm.deals.board.messages.handoffSuccess"), "success");
      await this.fetchDeals();
    },
    handleWonHandoffError(payload = {}) {
      this.showSnackbar(
        payload?.message || this.$t("ncrm.deals.board.messages.handoffError"),
        payload?.color || "error"
      );
    },
    async requestDealMoveToStage({ dealId, targetStageId } = {}) {
      const normalizedDealId = parsePositiveId(dealId);
      const normalizedTargetStageId = parsePositiveId(targetStageId);
      if (!normalizedDealId || !normalizedTargetStageId) return;
      const targetStage = this.resolveStageById(normalizedTargetStageId);
      const targetStageType = String(targetStage?.stage_type || "").trim().toLowerCase();
      const dealSnapshot = (this.deals || []).find((deal) => parsePositiveId(deal?.id) === normalizedDealId) || null;
      if (targetStageType === "won") {
        this.openWonHandoffDialog({
          dealId: normalizedDealId,
          targetStageId: normalizedTargetStageId,
        });
        return;
      }
      if (targetStageType === "lost") {
        const closeOpenActivities = resolveDealLostTaskClosureConfirmation({ stage: targetStage, record: dealSnapshot, translate: this.$t, entityLabel: this.resolveDealNameById(normalizedDealId) });
        if (closeOpenActivities === null) return;
        this.pendingLostStageMove = {
          dealId: normalizedDealId,
          dealName: this.resolveDealNameById(normalizedDealId),
          targetStageId: normalizedTargetStageId,
          targetStageType,
          closeOpenActivities,
        };
        this.lostReasonDialog = true;
        return;
      }
      await this.executeDealStageMove({ dealId: normalizedDealId, targetStageId: normalizedTargetStageId, reason: null });
    },
    closeLostReasonDialog() {
      this.lostReasonDialog = false;
      this.pendingLostStageMove = null;
    },
    async confirmLostReason(reason) {
      const pending = this.pendingLostStageMove || {};
      const dealId = Number(pending?.dealId || 0) || null;
      const targetStageId = Number(pending?.targetStageId || 0) || null;
      const normalizedReason = String(reason || "").trim();
      if (!dealId || !targetStageId || !normalizedReason) return;
      await this.executeDealStageMove({
        dealId,
        targetStageId,
        reason: normalizedReason,
        closeOpenActivities: pending?.closeOpenActivities === true,
      });
      this.closeLostReasonDialog();
    },
    async executeDealStageMove({ dealId, targetStageId, reason = null, closeOpenActivities = false } = {}) {
      const normalizedDealId = Number(dealId || 0) || null;
      const normalizedTargetStageId = Number(targetStageId || 0) || null;
      if (!normalizedDealId || !normalizedTargetStageId) return;
      this.movingDealId = normalizedDealId;
      try {
        const payload = closeOpenActivities
          ? { stage_id: normalizedTargetStageId, reason: String(reason || "").trim() || null, close_open_activities: true }
          : { stage_id: normalizedTargetStageId, reason: String(reason || "").trim() || null };
        await axios.patch(`/ncrm/deals/${normalizedDealId}/stage`, payload);
        await this.fetchDeals();
      } catch (error) {
        console.error("[ncrm-deal-board] move stage error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.moveError"),
          "error"
        );
      } finally {
        this.movingDealId = null;
      }
    },
    async openCreateDealDialog() {
      try {
        await Promise.all([
          this.ownerOptions.length ? Promise.resolve(this.ownerOptions) : this.fetchOwners(),
          this.fetchCreateDealProductOptions(""),
          this.fetchCreateDealCompanyOptions(""),
          this.fetchCreateDealPersonOptions(""),
        ]);
        const firstStageId = Number(this.stages?.[0]?.id || 0) || null;
        const firstOwnerId = Number(this.ownerOptions?.[0]?.id || 0) || null;
        this.createDealForm = {
          name: "",
          owner_user_id: firstOwnerId,
          stage_id: firstStageId,
          product_ids: [],
          expected_close_date: "",
          ncrm_company_id: null,
          ncrm_person_id: null,
          notes: "",
        };
        this.createDealDialog = true;
      } catch (error) {
        console.error("[ncrm-deal-board] open create deal dialog error", error);
      }
    },
    closeCreateDealDialog() {
      this.createDealDialog = false;
    },
    async submitCreateDeal() {
      const name = String(this.createDealForm?.name || "").trim();
      const ownerUserId = Number(this.createDealForm?.owner_user_id || 0) || null;
      const stageId = Number(this.createDealForm?.stage_id || 0) || null;
      const companyId = Number(this.createDealForm?.ncrm_company_id || 0) || null;
      const personId = Number(this.createDealForm?.ncrm_person_id || 0) || null;
      const selectedProductIds = Array.from(
        new Set(
          (Array.isArray(this.createDealForm?.product_ids) ? this.createDealForm.product_ids : [])
            .map((value) => Number(value || 0))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      if (!name || !ownerUserId || !stageId) {
        this.showSnackbar(this.$t("ncrm.deals.board.messages.createValidation"), "warning");
        return;
      }
      if (!companyId && !personId) {
        this.showSnackbar(this.$t("ncrm.deals.board.messages.contactRequired"), "warning");
        return;
      }

      this.createDealLoading = true;
      try {
        const estimatedValue = this.estimateCreateDealExpectedValue(selectedProductIds);
        const { data } = await axios.post("/ncrm/deals", {
          name,
          owner_user_id: ownerUserId,
          pipeline_id: this.boardId,
          stage_id: stageId,
          ncrm_company_id: companyId || null,
          ncrm_person_id: personId || null,
          expected_value: estimatedValue,
          expected_close_date: this.createDealForm.expected_close_date || null,
          notes: this.createDealForm.notes || null,
        });
        const createdDealId = Number(data?.id || 0) || null;
        if (createdDealId && selectedProductIds.length) {
          const attachResult = await this.attachProductsToDeal({
            dealId: createdDealId,
            productIds: selectedProductIds,
          });
          if (attachResult.failedCount > 0) {
            this.showSnackbar(
              this.$t("ncrm.deals.board.messages.productsAttachPartial", {
                failed: attachResult.failedCount,
              }),
              "warning"
            );
          }
        }
        this.showSnackbar(this.$t("ncrm.deals.board.messages.dealCreated"), "success");
        this.closeCreateDealDialog();
        await Promise.all([this.fetchBoard(), this.fetchDeals()]);
      } catch (error) {
        console.error("[ncrm-deal-board] create deal error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.messages.createError"),
          "error"
        );
      } finally {
        this.createDealLoading = false;
      }
    },
    estimateCreateDealExpectedValue(productIds = []) {
      if (!Array.isArray(productIds) || !productIds.length) return null;
      let hasPricedProduct = false;
      let total = 0;
      for (const productId of productIds) {
        const product = this.createDealProductCatalogById?.[productId];
        if (!product) continue;
        const unitPrice = toNumber(product.default_unit_price, null);
        if (unitPrice === null) continue;
        hasPricedProduct = true;
        total += unitPrice;
      }
      if (!hasPricedProduct) return null;
      return Number(total.toFixed(2));
    },
    async attachProductsToDeal({ dealId, productIds = [] } = {}) {
      const normalizedDealId = Number(dealId || 0) || null;
      const normalizedProductIds = Array.from(
        new Set(
          (Array.isArray(productIds) ? productIds : [])
            .map((value) => Number(value || 0))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!normalizedDealId || !normalizedProductIds.length) {
        return { failedCount: 0, totalCount: 0 };
      }
      const settled = await Promise.allSettled(
        normalizedProductIds.map((productId) =>
          axios.post(`/ncrm/deals/${normalizedDealId}/line-items`, {
            product_id: productId,
            quantity: 1,
          })
        )
      );
      const failedCount = settled.filter((item) => item.status === "rejected").length;
      return {
        failedCount,
        totalCount: normalizedProductIds.length,
      };
    },
    handleCreateDealProductSearch(search) {
      if (this.createDealProductSearchDebounceId) clearTimeout(this.createDealProductSearchDebounceId);
      this.createDealProductSearchDebounceId = setTimeout(() => {
        this.fetchCreateDealProductOptions(search);
      }, 220);
    },
    handleCreateDealCompanySearch(search) {
      if (this.createDealCompanySearchDebounceId) clearTimeout(this.createDealCompanySearchDebounceId);
      this.createDealCompanySearchDebounceId = setTimeout(() => {
        this.fetchCreateDealCompanyOptions(search);
      }, 220);
    },
    handleCreateDealPersonSearch(search) {
      if (this.createDealPersonSearchDebounceId) clearTimeout(this.createDealPersonSearchDebounceId);
      this.createDealPersonSearchDebounceId = setTimeout(() => {
        this.fetchCreateDealPersonOptions(search);
      }, 220);
    },
    async fetchCreateDealProductOptions(search = "") {
      const requestSeq = ++this.createDealProductRequestSeq;
      this.createDealProductsLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/products", {
          params: {
            search: normalizedSearch || undefined,
            limit: 50,
            is_active: true,
          },
        });
        if (requestSeq !== this.createDealProductRequestSeq) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const nextCatalogById = { ...this.createDealProductCatalogById };
        this.createDealProductOptions = items.map((item) => {
          const id = Number(item?.id || 0);
          const name = String(item?.name || "").trim() || `#${id}`;
          const productType = normalizeDealMetaLabel(item?.product_type);
          const billingPeriod = resolveDealBillingMetaLabel(item?.product_type, item?.billing_period);
          const suffix = [productType, billingPeriod].filter(Boolean).join(" • ");
          const label = suffix ? `${name} (${suffix})` : name;
          nextCatalogById[id] = item;
          return {
            id,
            label,
          };
        });
        this.createDealProductCatalogById = nextCatalogById;
      } catch (error) {
        console.error("[ncrm-deal-board] load create deal products error", error);
        if (requestSeq === this.createDealProductRequestSeq) {
          this.createDealProductOptions = [];
        }
      } finally {
        if (requestSeq === this.createDealProductRequestSeq) {
          this.createDealProductsLoading = false;
        }
      }
    },
    async fetchCreateDealCompanyOptions(search = "") {
      const requestSeq = ++this.createDealCompanyRequestSeq;
      this.createDealCompaniesLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "company",
            search: normalizedSearch || undefined,
            page: 1,
            perPage: 25,
          },
        });
        if (requestSeq !== this.createDealCompanyRequestSeq) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        this.createDealCompanyOptions = items.map((item) => {
          const id = Number(item?.id || 0);
          const name = String(item?.name || "").trim() || `#${id}`;
          const email = String(item?.email_primary || "").trim();
          const phone = String(item?.phone_primary || "").trim();
          const detail = [email, phone].filter(Boolean).join(" • ");
          return {
            id,
            label: detail ? `${name} (${detail})` : name,
          };
        });
      } catch (error) {
        console.error("[ncrm-deal-board] load create deal companies error", error);
        if (requestSeq === this.createDealCompanyRequestSeq) {
          this.createDealCompanyOptions = [];
        }
      } finally {
        if (requestSeq === this.createDealCompanyRequestSeq) {
          this.createDealCompaniesLoading = false;
        }
      }
    },
    async fetchCreateDealPersonOptions(search = "") {
      const requestSeq = ++this.createDealPersonRequestSeq;
      this.createDealPeopleLoading = true;
      try {
        const normalizedSearch = String(search || "").trim();
        const { data } = await axios.get("/ncrm/contacts", {
          params: {
            type: "person",
            search: normalizedSearch || undefined,
            page: 1,
            perPage: 25,
          },
        });
        if (requestSeq !== this.createDealPersonRequestSeq) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        this.createDealPersonOptions = items.map((item) => {
          const id = Number(item?.id || 0);
          const firstName = String(item?.first_name || "").trim();
          const lastName = String(item?.last_name || "").trim();
          const fullName = `${firstName} ${lastName}`.trim() || `#${id}`;
          const company = String(item?.company_name || "").trim();
          const email = String(item?.email_primary || "").trim();
          const detail = [company, email].filter(Boolean).join(" • ");
          return {
            id,
            label: detail ? `${fullName} (${detail})` : fullName,
          };
        });
      } catch (error) {
        console.error("[ncrm-deal-board] load create deal people error", error);
        if (requestSeq === this.createDealPersonRequestSeq) {
          this.createDealPersonOptions = [];
        }
      } finally {
        if (requestSeq === this.createDealPersonRequestSeq) {
          this.createDealPeopleLoading = false;
        }
      }
    },
    async fetchBoardMembers() {
      if (!this.boardId) return;
      this.memberLoading = true;
      try {
        const { data } = await axios.get(`/ncrm/deals/boards/${this.boardId}/members`);
        this.members = Array.isArray(data?.items) ? data.items : [];
      } catch (error) {
        console.error("[ncrm-deal-board] load board members error", error);
        this.members = [];
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.members.messages.addError"),
          "error"
        );
      } finally {
        this.memberLoading = false;
      }
      return this.members;
    },
    async openBoardSettingsDialog() {
      this.memberUserId = null;
      this.memberRole = "member";
      this.boardSettingsTab = "members";
      this.initializeBoardRulesFromBoard();
      this.clearBoardRulesFeedback();
      this.boardSettingsDialog = true;
      try {
        await Promise.all([this.fetchOwners(), this.fetchBoardMembers(), this.fetchBoardAudit({ force: true, reset: true })]);
        this.initializeBoardRulesFromBoard();
      } catch (error) {
        console.error("[ncrm-deal-board] open board settings load error", error);
      }
    },
    openDeleteBoardDialog() {
      if (!this.boardId) return;
      this.deleteBoardConfirmText = "";
      const hadBoardSettingsOpen = this.boardSettingsDialog === true;
      this.restoreBoardSettingsAfterDeleteDialog = hadBoardSettingsOpen;
      if (hadBoardSettingsOpen) {
        this.boardSettingsDialog = false;
      }
      if (this.deleteDialogOpenTimerId) {
        clearTimeout(this.deleteDialogOpenTimerId);
      }
      this.deleteDialogOpenTimerId = setTimeout(() => {
        this.deleteBoardDialog = true;
        this.deleteDialogOpenTimerId = null;
      }, hadBoardSettingsOpen ? 120 : 0);
    },
    async confirmDeleteBoard() {
      if (!this.boardId) return;
      this.deleteBoardLoading = true;
      try {
        await axios.delete(`/ncrm/deals/boards/${this.boardId}`);
        this.restoreBoardSettingsAfterDeleteDialog = false;
        this.deleteBoardDialog = false;
        this.boardSettingsDialog = false;
        this.showSnackbar(this.$t("ncrm.deals.board.delete.messages.success"), "success");
        this.$router.replace({ name: "NcrmDealsHome" }).catch(() => {});
      } catch (error) {
        console.error("[ncrm-deal-board] delete board error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.delete.messages.deleteError"),
          "error"
        );
      } finally {
        this.deleteBoardLoading = false;
      }
    },
    async saveBoardRules() {
      if (!this.boardId) return;
      this.boardRulesLoading = true;
      try {
        const { data } = await axios.patch(`/ncrm/deals/boards/${this.boardId}`, {
          auto_create_task_if_missing_after_change:
            this.boardRulesForm.autoCreateTaskIfMissingAfterChange === true,
          auto_create_task_if_missing_product:
            this.boardRulesForm.autoCreateTaskIfMissingProduct === true,
          auto_create_task_if_missing_delay_minutes: this.resolveBoardAutoMissingTaskDelayMinutes(
            this.boardRulesForm
          ),
        });
        const board = data?.board || null;
        if (board) {
          this.board = board;
          this.stages = Array.isArray(board?.stages) ? board.stages : [];
          this.syncStageVisibleCounts(this.stages);
          this.initializeBoardRulesFromBoard(board);
        }
        this.setBoardRulesFeedback(this.$t("ncrm.deals.board.settings.rules.messages.saved"), "success");
      } catch (error) {
        console.error("[ncrm-deal-board] save board rules error", error);
        this.setBoardRulesFeedback(
          error?.response?.data?.error || this.$t("ncrm.deals.board.settings.rules.messages.saveError"),
          "error"
        );
      } finally {
        this.boardRulesLoading = false;
      }
    },
    clearBoardAuditFilters() {
      const hadChanges = this.boardAuditEventType !== "all" ||
        Number(this.boardAuditChangedByUserId || 0) > 0 ||
        String(this.boardAuditFromDate || "").trim() ||
        String(this.boardAuditToDate || "").trim();

      this.boardAuditSuppressFilterReload = true;
      this.boardAuditEventType = "all";
      this.boardAuditChangedByUserId = null;
      this.boardAuditFromDate = "";
      this.boardAuditToDate = "";
      if (typeof this.$nextTick === "function") {
        this.$nextTick(() => {
          this.boardAuditSuppressFilterReload = false;
        });
      } else {
        this.boardAuditSuppressFilterReload = false;
      }

      if (hadChanges && this.boardSettingsDialog && this.boardId) {
        this.fetchBoardAudit({ force: true, reset: true });
      }
    },
    handleBoardAuditFilterChange() {
      if (this.boardAuditSuppressFilterReload) return;
      if (!this.boardSettingsDialog || this.boardSettingsTab !== "audit") return;
      this.fetchBoardAudit({ force: true, reset: true });
    },
    buildBoardAuditRequestParams({ page }) {
      const params = {
        limit: this.boardAuditPageSize,
        page,
      };
      const eventType = String(this.boardAuditEventType || "").trim().toLowerCase();
      if (eventType && eventType !== "all") {
        params.eventType = eventType;
      }
      const changedByUserId = Number(this.boardAuditChangedByUserId || 0) || null;
      if (changedByUserId) {
        params.changedByUserId = changedByUserId;
      }
      const fromDate = String(this.boardAuditFromDate || "").trim();
      if (fromDate) {
        params.fromDate = fromDate;
      }
      const toDate = String(this.boardAuditToDate || "").trim();
      if (toDate) {
        params.toDate = toDate;
      }
      return params;
    },
    async fetchBoardAudit({ force = false, reset = false, append = false } = {}) {
      if (!this.boardId) return;
      if ((this.boardAuditLoading || this.boardAuditLoadingMore) && !force) return;
      if (append && this.boardAuditTotal <= this.boardAuditItems.length) return;

      const requestPage = append ? this.boardAuditPage + 1 : 1;
      if (reset && !append) {
        this.boardAuditItems = [];
        this.boardAuditTotal = 0;
        this.boardAuditPage = 0;
      }

      if (append) {
        this.boardAuditLoadingMore = true;
      } else {
        this.boardAuditLoading = true;
      }

      try {
        const { data } = await axios.get(`/ncrm/deals/boards/${this.boardId}/audit`, {
          params: this.buildBoardAuditRequestParams({ page: requestPage }),
        });
        const incomingItems = Array.isArray(data?.items) ? data.items : [];
        this.boardAuditItems = append ? [...this.boardAuditItems, ...incomingItems] : incomingItems;
        const rawTotal = Number(data?.total);
        const normalizedTotal = Number.isFinite(rawTotal) && rawTotal >= 0 ? Math.trunc(rawTotal) : this.boardAuditItems.length;
        this.boardAuditTotal = Math.max(normalizedTotal, this.boardAuditItems.length);
        const rawPage = Number(data?.page);
        this.boardAuditPage = Number.isFinite(rawPage) && rawPage > 0 ? Math.trunc(rawPage) : requestPage;
        const rawPerPage = Number(data?.perPage);
        if (Number.isFinite(rawPerPage) && rawPerPage > 0) {
          this.boardAuditPageSize = Math.trunc(rawPerPage);
        }
      } catch (error) {
        console.error("[ncrm-deal-board] load board audit error", error);
        if (!append) {
          this.boardAuditItems = [];
          this.boardAuditTotal = 0;
          this.boardAuditPage = 0;
        }
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.audit.messages.loadError"),
          "error"
        );
      } finally {
        if (append) {
          this.boardAuditLoadingMore = false;
        } else {
          this.boardAuditLoading = false;
        }
      }
    },
    loadMoreBoardAudit() {
      this.fetchBoardAudit({ force: true, append: true });
    },
    async addBoardMember() {
      const targetUserId = Number(this.memberUserId || 0) || null;
      if (!targetUserId) {
        this.showSnackbar(this.$t("ncrm.deals.board.members.messages.userRequired"), "warning");
        return;
      }

      this.memberLoading = true;
      try {
        await axios.post(`/ncrm/deals/boards/${this.boardId}/members`, {
          user_id: targetUserId,
          role_in_board: this.memberRole,
        });
        this.memberUserId = null;
        this.memberRole = "member";
        await Promise.all([this.fetchBoardMembers(), this.fetchBoard()]);
      } catch (error) {
        console.error("[ncrm-deal-board] add member error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.members.messages.addError"),
          "error"
        );
      } finally {
        this.memberLoading = false;
      }
    },
    async removeBoardMember(memberUserId) {
      const userId = Number(memberUserId || 0) || null;
      if (!userId) return;
      this.memberLoading = true;
      try {
        await axios.delete(`/ncrm/deals/boards/${this.boardId}/members/${userId}`);
        await Promise.all([this.fetchBoardMembers(), this.fetchBoard()]);
      } catch (error) {
        console.error("[ncrm-deal-board] remove member error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.board.members.messages.removeError"),
          "error"
        );
      } finally {
        this.memberLoading = false;
      }
    },
    tableRowProps() {
      return { style: "cursor: pointer" };
    },
    columnAccentColor(index) {
      const colors = ["primary", "info", "success", "warning", "tertiary", "secondary"];
      return colors[index % colors.length];
    },
    columnAccentStyle(index) {
      const color = this.columnAccentColor(index);
      return `rgb(var(--v-theme-${color}))`;
    },
    cycleDensity() {
      if (this.densityCycleDisabled) return;
      this.effectiveDensity = this.nextDensity;
    },
  },
};
</script>

<style scoped>
.ncrm-deal-board {
  text-align: left;
}

.ncrm-deal-board__columns {
  display: flex;
  gap: 0;
  overflow-x: auto;
  overflow-y: hidden;
  align-items: flex-start;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  background: var(--ncrm-color-bg-card);
  height: calc(100vh - var(--v-layout-top, 64px) - 196px);
  min-height: 420px;
}

.ncrm-deal-board__columns--comfortable .ncrm-deal-board__column {
  min-width: 320px;
}

.ncrm-deal-board__columns--standard .ncrm-deal-board__column {
  min-width: 300px;
}

.ncrm-deal-board__columns--compact .ncrm-deal-board__column {
  min-width: 276px;
}

.ncrm-deal-board__columns--micro .ncrm-deal-board__column {
  min-width: 248px;
}

.ncrm-deal-board__column {
  flex: 0 0 auto;
  position: relative;
  padding: 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.ncrm-deal-board__column--standard {
  padding: 9px;
}

.ncrm-deal-board__column--compact {
  padding: 8px;
}

.ncrm-deal-board__column--micro {
  padding: 6px;
}

.ncrm-deal-board__column-accent {
  height: 4px;
  border-radius: 3px;
  margin-bottom: 10px;
  flex-shrink: 0;
  opacity: 0.7;
}

.ncrm-deal-board__column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
}

.ncrm-deal-board__column-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
  transition: background-color 0.15s ease, outline-color 0.15s ease;
}

.ncrm-deal-board__column-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-deal-board__column-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px 8px;
  color: rgba(var(--v-theme-on-surface), 0.28);
  font-size: 0.82rem;
  text-align: center;
}

.ncrm-deal-board__column-empty-icon {
  opacity: 0.35;
}

.ncrm-deal-board__column--drop-target .ncrm-deal-board__column-body {
  background-color: var(--ncrm-color-drag-target-bg);
  border-radius: calc(var(--app-radius-base) - 2px);
  outline: 1px dashed var(--ncrm-color-drag-target-outline);
  outline-offset: -1px;
}

.ncrm-deal-board__column--drag-source .ncrm-deal-board__column-body {
  background-color: var(--ncrm-color-drag-source-bg);
  border-radius: calc(var(--app-radius-base) - 2px);
}

.ncrm-deal-board__column-divider {
  position: absolute;
  top: 8px;
  right: 0;
  width: 1px;
  height: calc(100% - 16px);
  background: var(--ncrm-color-border-default);
}

.ncrm-deal-board__entry {
  border-color: var(--ncrm-color-border-default);
  align-items: flex-start;
  cursor: grab;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ncrm-deal-board__entry:hover {
  border-color: var(--ncrm-color-primary-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  transform: translateY(-1px);
}

.ncrm-deal-board__entry:active {
  cursor: grabbing;
}

.ncrm-deal-board__entry--dragging {
  opacity: 0.45;
}

.ncrm-deal-board__entry--overdue {
  border-left: 3px solid rgb(var(--v-theme-error)) !important;
}

.ncrm-deal-board__entry-status-chip {
  margin-top: 1px;
}

.ncrm-deal-board__entry--compact .ncrm-deal-board__entry-fields,
.ncrm-deal-board__entry--micro .ncrm-deal-board__entry-fields {
  gap: 2px;
}

.ncrm-deal-board__entry--micro .ncrm-deal-board__entry-title {
  font-size: 0.95rem;
}

.ncrm-deal-board__entry--compact .ncrm-deal-board__entry-title {
  font-size: 1rem;
}

.ncrm-deal-board__entry-title {
  min-width: 0;
  flex: 1 1 auto;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.15;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ncrm-deal-board__entry-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ncrm-deal-board__entry-field {
  line-height: 1.15;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.ncrm-deal-board__entry-field-label {
  flex: 0 0 auto;
  white-space: nowrap;
}

.ncrm-deal-board__entry-field-value {
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-deal-board__entry-field-value--overdue {
  color: rgb(var(--v-theme-error));
  font-weight: 500;
}

.ncrm-deal-board__entry-field-chip {
  max-width: 100%;
}

.ncrm-deal-board__entry-field-chip :deep(.v-chip__content) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ncrm-deal-board__entry-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 6px;
  padding-top: 5px;
  border-top: 1px solid var(--ncrm-color-border-subtle);
}

.ncrm-deal-board__entry-footer-timestamp {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.74rem;
  color: rgba(var(--v-theme-on-surface), 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.ncrm-deal-board__entry-footer-icon {
  opacity: 0.38;
  flex-shrink: 0;
}

.ncrm-deal-board__table-cell-inline {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ncrm-deal-board__table-cell-icon {
  opacity: 0.55;
  flex-shrink: 0;
}

.ncrm-deal-board__table-chevron {
  opacity: 0;
  transition: opacity 0.15s ease;
}

:deep(.ncrm-deal-board__table .v-data-table__tr:hover .ncrm-deal-board__table-chevron) {
  opacity: 1;
}

:deep(.ncrm-deal-board__table .v-data-table__th--sortable .v-data-table-header__sort-icon) {
  opacity: 0.3;
  transition: opacity 0.12s ease, color 0.12s ease;
}

:deep(.ncrm-deal-board__table .v-data-table__th--sorted .v-data-table-header__sort-icon) {
  opacity: 1;
  color: rgb(var(--v-theme-primary));
}

:deep(.ncrm-deal-board__table .v-data-table__th--sorted .v-data-table-header__content) {
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
}

.ncrm-deal-board__load-more {
  align-self: center;
}

.ncrm-deal-board__member-role {
  min-width: 170px;
}

.ncrm-deal-board__member-user-field {
  min-width: 250px;
}

.ncrm-deal-board__audit-filters {
  margin-top: 8px;
  align-items: start;
}

.ncrm-deal-board__audit-actions {
  margin-top: 4px;
}

.ncrm-deal-board__reset-user-col {
  min-height: 56px;
}

.ncrm-deal-board__reset-user-btn {
  white-space: nowrap;
}

.ncrm-deal-board__card-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ncrm-deal-board__card-field-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 6px;
}

@media (max-width: 960px) {
  .ncrm-deal-board__columns {
    height: auto;
    min-height: 0;
    max-height: none;
    overflow-y: visible;
  }

  .ncrm-deal-board__column {
    height: auto;
  }

  .ncrm-deal-board__column-body {
    overflow-y: visible;
  }
}
</style>
